<template>
	<div>
		<block-designer
			v-if="isEditDisplayMode && !lessonPlanController.editLayouts"
			:blockDefinitions="blockDefinitions"
			:blocks="blocks"
			:slide-id="slideId"
			:defaultBlockStyles="lessonPlanController.lessonPlanContents.settings.styles.block"
			:settingStyles="lessonPlanController.lessonPlanContents.settings.styles"
			:isBlockSelected.sync="isBlockSelected"
			:leftPanelsTop="leftPanelsTop"
			:leftPanelsBottom="leftPanelsBottom"
			:rightPanels="rightPanels"
			:grid-size="lessonPlanController.gridSize"
			:available-stencils="lessonPlanController.availableStencils"
			:globalVariables="lessonPlanController.globalVariables"
			@create:draft="$emit('create:draft', $event)"
			@slideChanged="slideChangeAction"
		>
		</block-designer>
		<layout-designer
			v-if="isEditDisplayMode && lessonPlanController.editLayouts"
			:blockDefinitions="blockDefinitions"
			:blocks="blocks"
			:slide-id="slideId"
			:defaultBlockStyles="lessonPlanController.lessonPlanContents.settings.styles.block"
			:settingStyles="lessonPlanController.lessonPlanContents.settings.styles"
			:isBlockSelected.sync="isBlockSelected"
			:leftPanelsTop="leftPanelsLayoutTop"
			:leftPanelsBottom="leftPanelsLayoutBottom"
			:grid-size="lessonPlanController.gridSize"
			:available-stencils="lessonPlanController.availableStencils"
			:dark="true"
			:imported="lessonPlanController.imported"
			@create:draft="$emit('create:draft', $event)"
			@slideChanged="slideChangeAction"
		>
		</layout-designer>
		<block-viewer
			v-if="isViewDisplayMode || isReviewDisplayMode || isPreviewDisplayMode"
			:blockDefinitions="blockDefinitions"
			:blocks="blocks"
			:slide-id="slideId"
			:defaultBlockStyles="lessonPlanController.lessonPlanContents.settings.styles.block"
			:settingStyles="lessonPlanController.lessonPlanContents.settings.styles"
			:leftPanelsTop="leftPanelsViewTop"
			:leftPanelsBottom="leftPanelsBottom"
			:rightPanels="rightPanels"
			:canStartPresenterMode="canStartPresenterModeViewer"
			@selectSlideById="lessonPlanController.navigation.selectSlideById($event)"
			@goToBookmarkTarget="lessonPlanController.goToBookmarkTarget($event)"
			:globalVariables="lessonPlanController.globalVariables"
			@slideChanged="slideChangeAction"
			:mode="mode"
		>
		</block-viewer>
		<block-activity
			v-if="isActivityDisplayMode"
			:blockDefinitions="blockDefinitions"
			:blocks="blocks"
			:slide-id="slideId"
			:defaultBlockStyles="lessonPlanController.lessonPlanContents.settings.styles.block"
			:settingStyles="lessonPlanController.lessonPlanContents.settings.styles"
			:leftPanelsTop="leftPanelsViewTop"
			:leftPanelsBottom="leftPanelsBottom"
			:rightPanels="rightPanels"
			:canStartPresenterMode="canStartPresenterModeActivity"
			@selectSlideById="lessonPlanController.navigation.selectSlideById($event)"
			@goToBookmarkTarget="lessonPlanController.goToBookmarkTarget($event)"
			:globalVariables="lessonPlanController.globalVariables"
			@slideChanged="slideChangeAction"
		>
		</block-activity>
		<v-snackbar
			v-model="lessonPlanController.notificationHandler.active"
			top
			:color="lessonPlanController.notificationHandler.color ? lessonPlanController.notificationHandler.color : 'error'"
			auto-height
		>
			{{ lessonPlanController.notificationHandler.text }}
			<v-btn flat @click="lessonPlanController.notificationHandler.active = false"> Close </v-btn>
		</v-snackbar>
		<v-snackbar v-model="isInstructorWindowActive" top color="info" :timeout="0" auto-height>
			Presenter Mode active
			<v-btn outline elevation="0" @click="lessonPlanController.stopDuplicateScreen()"> Stop </v-btn>
		</v-snackbar>
	</div>
</template>

<script>
import BlockDesigner from './blocks/_components/modes/edit/BlockDesigner.vue';
import LayoutDesigner from './blocks/_components/modes/edit/LayoutDesigner.vue';
import BlockViewer from './blocks/_components/modes/view/BlockViewer.vue';
import BlockActivity from './blocks/_components/modes/activity/BlockActivity.vue';
import blockDefinitions from './blocks/index.js';
import lessonPlanController from './controllers/LessonPlanController/index.js';
import { createHideSlides } from './controllers/HideSlides/hideSlidesFactory';
import { upgradeModel, resetVisited } from './models/upgradeModel.js';

import OutlinePanel from './components/slides/OutlinePanel.vue';
import LessonSettingsPanel from './components/slides/styles/LessonSettingsPanel.vue';
import GalleryPanel from './components/gallery/GalleryPanel.vue';
import LayoutsPanel from './components/layouts/LayoutsPanel.vue';
import ObjectivesPanel from './components/slides/objectives/ObjectivesPanel.vue';
import SlideProperties from './components/slides/properties/SlideProperties.vue';
import QuizProperties from './components/slides/quiz/QuizProperties.vue';
import activity from './mixins/activity.vue';
import { mapState } from 'vuex';
import { STORE_CONTENT_NAME, STATE_REVISION_CHANGED_TOGGLE } from '../../store_constants/content';
import InstructionalSettings from './components/settings/InstructionalSettings.vue';
import GlossaryPanel from '../commonInteractive/glossary/GlossaryPanel';
import glossaryController from '../commonInteractive/glossary/GlossaryController';
import PerformancePanel from './components/performance/PerformancePanel.vue';
import uuid from '../../utils/uuid';
import presenterModeMixin from './mixins/PresenterMode';
import SpeakerNotesPanelIcon from './components/slides/notes/SpeakerNotesPanelIcon.vue';
import AudienceNotesPanelIcon from './components/slides/notes/AudienceNotesPanelIcon.vue';
import VideoNotesPanelIcon from './components/slides/notes/VideoNotesPanelIcon.vue';
import SpeakerNotesPanel from './components/slides/notes/SpeakerNotesPanel.vue';
import AudienceNotesPanel from './components/slides/notes/AudienceNotesPanel.vue';
import VideoPanel from './blocks/video/VideoPanel.vue';
import ScormPayloadInfo from './components/settings/ScormPayloadInfo.vue';
import ScormPayloadInfoIcon from './components/settings/ScormPayloadInfoIcon.vue';
import { scormEventBus } from './components/settings/scormBus';
import { compress } from './components/settings/compressState';
import AiGeneratedContentDisclaimer from './components/slides/bertha/AiGeneratedContentDisclaimer';
import { STATE_PERMISSIONS_BY_CATEGORY, STORE_PERMISSIONS_NAME } from '../../store_constants/permissions';
import { STATE_USER_PERMISSONS, STORE_USER_NAME } from '../../store_constants/user';
import { RevisionDisplayMode } from 'plant-common/src/models/revisionDisplayMode';

export default {
	name: 'LessonPlan',
	props: ['value', 'view', 'mode', 'comments', 'activityState', 'isStarted', 'isScorm'],
	components: { BlockDesigner, LayoutDesigner, BlockViewer, BlockActivity },
	mixins: [activity, presenterModeMixin],
	data() {
		const lpController = lessonPlanController(this);

		return {
			lessonPlanController: lpController,
			hideSlides: this.mode !== RevisionDisplayMode.edit ? createHideSlides(lpController) : null,
			blockDefinitions,
			isBlockSelected: false,
			isDraft: false,
			leftPanelsTop: [
				{
					title: 'Outline',
					icon: 'mdi-file-tree',
					component: OutlinePanel
				},
				{
					title: 'Block Gallery',
					icon: 'mdi-toy-brick-outline',
					component: GalleryPanel,
					id: 'blockGallery'
				},
				{
					title: 'Glossary',
					icon: 'mdi-book-alphabet',
					component: GlossaryPanel,
					props: {
						mode: this.mode,
						value: this.value,
						controller: glossaryController(this)
					}
				}
			],
			leftPanelsLayoutTop: [
				{
					title: 'Layout',
					icon: 'mdi-file-tree',
					component: LayoutsPanel
				}
			],
			leftPanelsLayoutBottom: [
				{
					title: 'Lesson Settings',
					icon: 'mdi-cog',
					component: LessonSettingsPanel,
					disabled: this.mode !== RevisionDisplayMode.edit
				}
			],
			addons: {
				glossary: {
					title: 'Glossary',
					icon: 'mdi-book-alphabet',
					component: GlossaryPanel,
					props: {
						mode: this.mode,
						value: this.value,
						controller: glossaryController(this)
					}
				},
				performance: {
					title: 'Objectives Level',
					icon: 'mdi-bullseye-arrow',
					component: PerformancePanel
				}
			}
		};
	},
	computed: {
		...mapState(STORE_CONTENT_NAME, {
			revisionChangedToggle: STATE_REVISION_CHANGED_TOGGLE
		}),
		...mapState(STORE_PERMISSIONS_NAME, {
			permissionsByCategory: STATE_PERMISSIONS_BY_CATEGORY
		}),
		...mapState(STORE_USER_NAME, {
			userPermissions: STATE_USER_PERMISSONS
		}),
		isActivityDisplayMode() {
			return this.mode === RevisionDisplayMode.activity;
		},
		isEditDisplayMode() {
			return this.mode === RevisionDisplayMode.edit;
		},
		isPreviewDisplayMode() {
			return this.mode === RevisionDisplayMode.preview;
		},
		isReviewDisplayMode() {
			return this.mode === RevisionDisplayMode.review;
		},
		isViewDisplayMode() {
			return this.mode === RevisionDisplayMode.view;
		},
		content() {
			return this.lessonPlanController.lessonPlanContents;
		},
		blocks() {
			return this.lessonPlanController.navigation.selectedSlide
				? this.lessonPlanController.navigation.selectedSlide.blocks
				: [];
		},
		hasSomeGlossary() {
			return (
				(this.value.body.contents.glossary && this.value.body.contents.glossary.length) ||
				(this.value.body.contents.glossaryImported && this.value.body.contents.glossaryImported.length) ||
				(this.interactiveTextController &&
					this.interactiveTextController.glossariesFromItsFromPage &&
					this.interactiveTextController.glossariesFromItsFromPage.length)
			);
		},
		slideId() {
			// Key for BlockCanvas, to repaint it
			// To avoid duplicate slide and don't repaint when changing to those
			return this.lessonPlanController.navigation.selectedSlide
				? this.lessonPlanController.navigation.selectedSlide.id
				: '0';
		},
		rightPanels() {
			let panels = [];

			if (!this.lessonPlanController.navigation.selectedSlide) {
				return panels;
			}

			if (this.isEditDisplayMode) {
				panels.push({
					title: 'Slide Properties Panel',
					icon: 'mdi-card-text-outline',
					component: SlideProperties
				});
			}

			const hasSpeakerPanel =
				this.isEditDisplayMode || (!this.isEditDisplayMode && this.lessonPlanController.hasSelectedSlideSpeakerNotes);
			const hasAudiencePanel =
				this.isEditDisplayMode || (!this.isEditDisplayMode && this.lessonPlanController.hasSelectedSlideAudienceNotes);
			const hasVideoPanel =
				this.isEditDisplayMode || (!this.isEditDisplayMode && this.lessonPlanController.hasSelectedSlideVideoNotes);

			if (hasSpeakerPanel) {
				panels.push({
					title: 'Speaker Notes',
					icon: SpeakerNotesPanelIcon,
					component: SpeakerNotesPanel
				});
			}

			if (hasAudiencePanel) {
				panels.push({
					title: 'Audience Notes',
					icon: AudienceNotesPanelIcon,
					component: AudienceNotesPanel
				});
			}

			if (hasVideoPanel) {
				panels.push({
					title: 'Video Notes',
					icon: VideoNotesPanelIcon,
					component: VideoPanel
				});
			}

			panels.push({
				title: 'Objectives',
				icon: 'mdi-bullseye-arrow',
				component: ObjectivesPanel
			});

			if (
				this.lessonPlanController.isQuiz(this.lessonPlanController.navigation.selectedSlide) &&
				this.isEditDisplayMode
			) {
				panels.unshift({
					title: 'Quiz Properties Panel',
					icon: 'mdi-book-education-outline',
					component: QuizProperties
				});
			}

			const hasAiDisclaimer = !this.isEditDisplayMode && this.lessonPlanController.aiGeneratedBlockTypes.length;

			if (hasAiDisclaimer) {
				panels.push({
					title: 'Bertha',
					icon: {
						template: '<v-img src="/img/bertha/bertha-mini-multicolor-LightMode.svg" height="24" contain></v-img>'
					},
					component: {
						template: AiGeneratedContentDisclaimer(this.lessonPlanController.aiGeneratedBlockTypes)
					}
				});
			}

			return panels;
		},
		leftPanelsViewTop() {
			const panels = [
				{
					title: 'Outline',
					icon: 'mdi-file-tree',
					component: OutlinePanel
				}
			];
			if (this.lessonPlanController.hasQuizInIP()) {
				panels.push(this.addons.performance);
			}
			if (!this.isEditDisplayMode && this.hasSomeGlossary) {
				panels.push(this.addons.glossary);
			}

			return panels;
		},
		leftPanelsBottom() {
			return [
				{
					title: 'Scorm Settings',
					icon: ScormPayloadInfoIcon,
					component: ScormPayloadInfo,
					disabled: this.hideScormSettings
				},
				{
					title: 'Instructional Settings',
					icon: 'mdi-school-outline',
					component: InstructionalSettings
				},
				{
					title: 'Lesson Settings',
					icon: 'mdi-cog',
					component: LessonSettingsPanel,
					disabled: this.lessonPlanController.mode !== RevisionDisplayMode.edit
				}
			];
		},
		hideScormSettings() {
			return !this.canShowSuspendDataSize;
		},
		canShowSuspendDataSize() {
			if (this.isScorm) return false;

			const permissions = [this.permissionsByCategory.workflowManagement.editViewWorkflowDrafts];

			return this.isDraft || this.checkUserPermissions(permissions);
		},
		canStartPresenterModeViewer() {
			const permissions = [this.permissionsByCategory.training.activatePresenterMode];

			return !this.isDraft && this.checkUserPermissions(permissions);
		},
		canStartPresenterModeActivity() {
			if (this.isScorm) return false;

			const permissions = [this.permissionsByCategory.training.activatePresenterMode];

			return !this.isDraft && this.checkUserPermissions(permissions);
		}
	},
	created() {
		this.initSlides();
		// create a new $origin for Interactive Presentation data
		if (this.needToCreateOrigin()) {
			this.value.body.contents.$origin = `plant:/files/${this.value._id}/${uuid()}.json`;
		}
		if (this.canShowSuspendDataSize) {
			this.lessonPlanController.initActivityState();
			this.lessonPlanController.compressedActivityState = compress(this.lessonPlanController.activityState);
			scormEventBus.$emit('scormDataSizeInBytes', this.lessonPlanController.compressedActivityState.length);
		}

		if (this.$router) {
			const urlPath = this.$router.app.$route.fullPath;
			this.isDraft = urlPath.includes('draft');
		}

		this.hideSlidesIfNeeded();
	},
	provide() {
		return {
			lessonPlanController: this.lessonPlanController,
			hideSlides: this.hideSlides,
			blockGlobals: {
				fontFamilies: this.lessonPlanController.fontFamilies
			}
		};
	},
	watch: {
		revisionChangedToggle() {
			// Recalculate slidesTree and selectedSlide
			// to repaint content after changing revision
			this.lessonPlanController.navigation.initSlidesTree();
			this.lessonPlanController.navigation.selectedSlide =
				this.lessonPlanController.navigation.slideArray[this.lessonPlanController.navigation.selectedSlideIndex];
		}
	},
	methods: {
		hideSlidesIfNeeded() {
			if (!this.isDuplicateScreenActive) {
				return;
			}

			if (this.hideSlides.isEmptyHiddenSlides()) {
				return;
			}

			this.hideSlides.toggleHideSlides();
		},
		initSlides() {
			if (
				(this.isEditDisplayMode && !this.lessonPlanController.editLayouts) ||
				this.isViewDisplayMode ||
				this.isReviewDisplayMode ||
				this.isActivityDisplayMode
			) {
				if (this.isStarted) {
					this.goToNextSlideAfterCompleted();
				} else {
					this.lessonPlanController.goToFirstSlide();
				}
				upgradeModel(this.content);
				resetVisited(this.content);
			}
		},
		slideChangeAction(blockCtrl) {
			blockCtrl.closeAllOverlays();
			blockCtrl.selectionClear();
		},
		needToCreateOrigin() {
			// New draft or old contents which don't have $origin
			if (!this.value.body.contents.$origin) {
				return true;
			}
			// It's a new revision of the content
			const [, , revision_id, filename] = this.value.body.contents.$origin.split('/');
			if (revision_id !== this.value._id) {
				return true;
			}
			return false;
		},
		checkUserPermissions(permissions) {
			const hasPermission = permissions.every(permission =>
				this.userPermissions.some(({ codename }) => codename === permission)
			);
			return hasPermission;
		}
	}
};
</script>

<style>
.lp-index {
	display: flex;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	overflow: hidden;
}

/* To remove a style in DiagramRightDrawer that is applied everywhere */
.lp-index.dark .v-text-field input {
	color: #ffffff !important;
}

.lp-index-panel.right-panel {
	right: 0;
}

.lp-index-panel.left-panel {
	left: 0;
}

.lp-index-panel {
	position: absolute;
	top: 50px;
	bottom: 0;
}

.lp-index-main {
	position: absolute;
	top: 50px;
	left: 80px;
	right: 80px;
	bottom: 0;
}
</style>
